import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { Modal } from '../../components/ui/Modal';
import { badgeClass, Field, FormActions, formatMoney, inputClass, primaryButtonClass, secondaryButtonClass } from '../../components/ui/FormControls';
import { cn } from '../../lib/utils';
import { Contractor, day, failed, hours, PayComponent, Shift, tdClass, thClass, useComponents, useContractors, useShifts } from './hrShared';

const Section: React.FC<{ title: string; description: string; action?: React.ReactNode; children: React.ReactNode }> = ({ title, description, action, children }) => (
  <section className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
    <div className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-200 px-4 py-3">
      <div>
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        <p className="text-xs text-gray-500">{description}</p>
      </div>
      {action}
    </div>
    {children}
  </section>
);

// ---- payroll settings: nothing is worked out until these are saved
const PayrollSettingsForm: React.FC<{ canEdit: boolean }> = ({ canEdit }) => {
  const queryClient = useQueryClient();
  const { data: saved, isLoading } = useQuery<Record<string, any> | null>({ queryKey: ['hr', 'payroll-settings'], queryFn: async () => (await api.get('/hr/payroll-settings')).data ?? null });
  const [form, setForm] = useState({ divisor: '', payOffDaysMonthly: '', payOffDaysDaily: '', overtimeMultiplier: '', overtimeBase: '', overtimeMinimumMinutes: '', roundNetTo: '' });
  useEffect(() => {
    if (saved) {
      setForm({
        divisor: String(saved.divisor),
        payOffDaysMonthly: saved.payOffDaysMonthly ? 'yes' : 'no',
        payOffDaysDaily: saved.payOffDaysDaily ? 'yes' : 'no',
        overtimeMultiplier: String(saved.overtimeMultiplier),
        overtimeBase: saved.overtimeBase,
        overtimeMinimumMinutes: String(saved.overtimeMinimumMinutes),
        roundNetTo: String(saved.roundNetTo),
      });
    }
  }, [saved]);
  const set = (key: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setForm((previous) => ({ ...previous, [key]: event.target.value }));
  const complete = Object.values(form).every((value) => value !== '');
  const save = useMutation({
    mutationFn: async () =>
      (await api.put('/hr/payroll-settings', {
        divisor: form.divisor === 'calendar' ? 'calendar' : Number(form.divisor),
        payOffDaysMonthly: form.payOffDaysMonthly === 'yes',
        payOffDaysDaily: form.payOffDaysDaily === 'yes',
        overtimeMultiplier: Number(form.overtimeMultiplier),
        overtimeBase: form.overtimeBase,
        overtimeMinimumMinutes: Number(form.overtimeMinimumMinutes),
        roundNetTo: Number(form.roundNetTo),
      })).data,
    onSuccess: () => {
      toast.success('Payroll settings saved');
      queryClient.invalidateQueries({ queryKey: ['hr', 'payroll-settings'] });
    },
    onError: failed('Could not save the settings'),
  });
  if (isLoading) return <p className="p-4 text-sm text-gray-500">Loading…</p>;
  return (
    <form className="space-y-4 p-4" onSubmit={(event) => { event.preventDefault(); if (complete) save.mutate(); }}>
      {!saved && <p className="rounded-md bg-amber-50 p-3 text-sm text-amber-800" data-testid="payroll-not-set">Payroll is not set up yet. Nothing is assumed: choose each of these as your company pays, then save. No payroll can be worked out before.</p>}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="Monthly pay is divided by" htmlFor="ps-divisor">
          <select id="ps-divisor" value={form.divisor} disabled={!canEdit} onChange={set('divisor')} className={inputClass}>
            <option value="">Choose</option>
            <option value="calendar">The days of the month</option>
            <option value="26">26 days</option>
            <option value="30">30 days</option>
          </select>
        </Field>
        <Field label="Weekly offs and holidays paid, monthly pay" htmlFor="ps-offs-monthly">
          <select id="ps-offs-monthly" value={form.payOffDaysMonthly} disabled={!canEdit} onChange={set('payOffDaysMonthly')} className={inputClass}><option value="">Choose</option><option value="yes">Yes</option><option value="no">No</option></select>
        </Field>
        <Field label="Weekly offs and holidays paid, daily wages" htmlFor="ps-offs-daily">
          <select id="ps-offs-daily" value={form.payOffDaysDaily} disabled={!canEdit} onChange={set('payOffDaysDaily')} className={inputClass}><option value="">Choose</option><option value="yes">Yes</option><option value="no">No</option></select>
        </Field>
        <Field label="Net pay rounded to" htmlFor="ps-round">
          <select id="ps-round" value={form.roundNetTo} disabled={!canEdit} onChange={set('roundNetTo')} className={inputClass}><option value="">Choose</option><option value="0.01">The paisa</option><option value="1">The rupee</option><option value="10">Ten rupees</option></select>
        </Field>
        <Field label="Overtime paid at (times the hourly rate)" htmlFor="ps-ot">
          <input id="ps-ot" type="number" min={0} max={5} step="0.25" value={form.overtimeMultiplier} disabled={!canEdit} onChange={set('overtimeMultiplier')} className={inputClass} />
        </Field>
        <Field label="Hourly rate worked out from" htmlFor="ps-ot-base">
          <select id="ps-ot-base" value={form.overtimeBase} disabled={!canEdit} onChange={set('overtimeBase')} className={inputClass}><option value="">Choose</option><option value="basic">The basic</option><option value="gross">The gross</option></select>
        </Field>
        <Field label="Overtime counted from (minutes a day)" htmlFor="ps-ot-min">
          <input id="ps-ot-min" type="number" min={0} max={240} value={form.overtimeMinimumMinutes} disabled={!canEdit} onChange={set('overtimeMinimumMinutes')} className={inputClass} />
        </Field>
      </div>
      {canEdit && (
        <FormActions>
          <button type="submit" disabled={!complete || save.isPending} className={primaryButtonClass}>{save.isPending ? 'Saving…' : 'Save Settings'}</button>
        </FormActions>
      )}
    </form>
  );
};

// ---- shifts
const ShiftForm: React.FC<{ shift: Shift | null; onDone: () => void }> = ({ shift, onDone }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ code: shift?.code ?? '', name: shift?.name ?? '', startTime: shift?.startTime ?? '09:00', endTime: shift?.endTime ?? '17:30', breakMinutes: String(shift?.breakMinutes ?? 30), graceMinutes: String(shift?.graceMinutes ?? 0), isActive: shift?.isActive ?? true });
  const save = useMutation({
    mutationFn: async () => {
      const body = { ...form, breakMinutes: Number(form.breakMinutes), graceMinutes: Number(form.graceMinutes) };
      return shift ? api.put(`/hr/shifts/${shift.id}`, body) : api.post('/hr/shifts', body);
    },
    onSuccess: () => {
      toast.success('Shift saved');
      queryClient.invalidateQueries({ queryKey: ['hr', 'shifts'] });
      onDone();
    },
    onError: failed('Could not save the shift'),
  });
  const set = (key: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement>) => setForm((previous) => ({ ...previous, [key]: event.target.value }));
  return (
    <form className="space-y-4" onSubmit={(event) => { event.preventDefault(); save.mutate(); }}>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <Field label="Code" htmlFor="sh-code"><input id="sh-code" value={form.code} onChange={(event) => setForm((p) => ({ ...p, code: event.target.value.toUpperCase() }))} maxLength={20} className={inputClass} /></Field>
        <Field label="Name" htmlFor="sh-name" className="sm:col-span-2"><input id="sh-name" value={form.name} onChange={set('name')} maxLength={60} className={inputClass} /></Field>
        <Field label="Starts" htmlFor="sh-start"><input id="sh-start" type="time" value={form.startTime} onChange={set('startTime')} className={inputClass} /></Field>
        <Field label="Ends" htmlFor="sh-end" hint="Before the start: runs past midnight"><input id="sh-end" type="time" value={form.endTime} onChange={set('endTime')} className={inputClass} /></Field>
        <Field label="Break (minutes)" htmlFor="sh-break"><input id="sh-break" type="number" min={0} max={240} value={form.breakMinutes} onChange={set('breakMinutes')} className={inputClass} /></Field>
        <Field label="Late after (minutes)" htmlFor="sh-grace"><input id="sh-grace" type="number" min={0} max={120} value={form.graceMinutes} onChange={set('graceMinutes')} className={inputClass} /></Field>
      </div>
      <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={form.isActive} onChange={(event) => setForm((p) => ({ ...p, isActive: event.target.checked }))} className="h-4 w-4 rounded border-gray-300 text-indigo-600" /> In use</label>
      <FormActions>
        <button type="button" onClick={onDone} className={secondaryButtonClass}>Cancel</button>
        <button type="submit" disabled={save.isPending} className={primaryButtonClass}>{save.isPending ? 'Saving…' : 'Save Shift'}</button>
      </FormActions>
    </form>
  );
};

// ---- contractors
const ContractorForm: React.FC<{ contractor: Contractor | null; onDone: () => void }> = ({ contractor, onDone }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    code: contractor?.code ?? '', name: contractor?.name ?? '', licenceNumber: contractor?.licenceNumber ?? '', licenceValidUntil: contractor?.licenceValidUntil ?? '',
    contactPerson: contractor?.contactPerson ?? '', phone: contractor?.phone ?? '', ratePerManDay: contractor?.ratePerManDay ?? '', ratePerOvertimeHour: contractor?.ratePerOvertimeHour ?? '', isActive: contractor?.isActive ?? true,
  });
  const save = useMutation({
    mutationFn: async () => {
      const body = { ...form, ratePerManDay: form.ratePerManDay === '' ? null : Number(form.ratePerManDay), ratePerOvertimeHour: form.ratePerOvertimeHour === '' ? null : Number(form.ratePerOvertimeHour) };
      return contractor ? api.put(`/hr/contractors/${contractor.id}`, body) : api.post('/hr/contractors', body);
    },
    onSuccess: () => {
      toast.success('Contractor saved');
      queryClient.invalidateQueries({ queryKey: ['hr', 'contractors'] });
      onDone();
    },
    onError: failed('Could not save the contractor'),
  });
  const set = (key: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement>) => setForm((previous) => ({ ...previous, [key]: event.target.value }));
  return (
    <form className="space-y-4" onSubmit={(event) => { event.preventDefault(); save.mutate(); }}>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <Field label="Code" htmlFor="co-code"><input id="co-code" value={form.code} onChange={(event) => setForm((p) => ({ ...p, code: event.target.value.toUpperCase() }))} maxLength={20} className={inputClass} /></Field>
        <Field label="Name" htmlFor="co-name" className="sm:col-span-2"><input id="co-name" value={form.name} onChange={set('name')} maxLength={100} className={inputClass} /></Field>
        <Field label="Licence number" htmlFor="co-licence"><input id="co-licence" value={form.licenceNumber} onChange={set('licenceNumber')} maxLength={60} className={inputClass} /></Field>
        <Field label="Licence valid until" htmlFor="co-licence-date"><input id="co-licence-date" type="date" value={form.licenceValidUntil} onChange={set('licenceValidUntil')} className={inputClass} /></Field>
        <Field label="Contact" htmlFor="co-contact"><input id="co-contact" value={form.contactPerson} onChange={set('contactPerson')} maxLength={100} className={inputClass} /></Field>
        <Field label="Phone" htmlFor="co-phone"><input id="co-phone" value={form.phone} onChange={set('phone')} maxLength={20} className={inputClass} /></Field>
        <Field label="Charge per man-day (₹)" htmlFor="co-rate" hint="As agreed with them"><input id="co-rate" type="number" min={0} step="0.01" value={form.ratePerManDay} onChange={set('ratePerManDay')} className={inputClass} /></Field>
        <Field label="Charge per overtime hour (₹)" htmlFor="co-ot"><input id="co-ot" type="number" min={0} step="0.01" value={form.ratePerOvertimeHour} onChange={set('ratePerOvertimeHour')} className={inputClass} /></Field>
      </div>
      <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={form.isActive} onChange={(event) => setForm((p) => ({ ...p, isActive: event.target.checked }))} className="h-4 w-4 rounded border-gray-300 text-indigo-600" /> In use</label>
      <FormActions>
        <button type="button" onClick={onDone} className={secondaryButtonClass}>Cancel</button>
        <button type="submit" disabled={save.isPending} className={primaryButtonClass}>{save.isPending ? 'Saving…' : 'Save Contractor'}</button>
      </FormActions>
    </form>
  );
};

// ---- pay components
const ComponentForm: React.FC<{ component: PayComponent | null; onDone: () => void }> = ({ component, onDone }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    code: component?.code ?? '', name: component?.name ?? '', kind: component?.kind ?? 'earning', method: component?.method ?? 'fixed', value: component?.value ?? '', base: component?.base ?? 'basic',
    ceiling: component?.ceiling ?? '', eligibleUpToGross: component?.eligibleUpToGross ?? '', prorate: component?.prorate ?? true, appliesTo: component?.appliesTo ?? 'all', sequence: String(component?.sequence ?? 100), isActive: component?.isActive ?? true,
  });
  const [slabs, setSlabs] = useState<{ upTo: string; amount: string }[]>(component?.slabs?.map((slab) => ({ upTo: slab.upTo === null ? '' : String(slab.upTo), amount: String(slab.amount) })) ?? [{ upTo: '', amount: '' }]);
  const save = useMutation({
    mutationFn: async () => {
      const body = {
        ...form,
        value: form.method === 'slab' ? 0 : Number(form.value || 0),
        ceiling: form.ceiling === '' ? null : Number(form.ceiling),
        eligibleUpToGross: form.eligibleUpToGross === '' ? null : Number(form.eligibleUpToGross),
        sequence: Number(form.sequence),
        slabs: form.method === 'slab' ? slabs.map((slab, index) => ({ upTo: index === slabs.length - 1 || slab.upTo === '' ? null : Number(slab.upTo), amount: Number(slab.amount || 0) })) : null,
      };
      return component ? api.put(`/hr/pay-components/${component.id}`, body) : api.post('/hr/pay-components', body);
    },
    onSuccess: () => {
      toast.success('Pay component saved');
      queryClient.invalidateQueries({ queryKey: ['hr', 'components'] });
      onDone();
    },
    onError: failed('Could not save the pay component'),
  });
  const set = (key: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setForm((previous) => ({ ...previous, [key]: event.target.value }));
  return (
    <form className="space-y-4" onSubmit={(event) => { event.preventDefault(); save.mutate(); }}>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <Field label="Code" htmlFor="pc-code"><input id="pc-code" value={form.code} onChange={(event) => setForm((p) => ({ ...p, code: event.target.value.toUpperCase() }))} maxLength={20} className={inputClass} /></Field>
        <Field label="Name on the payslip" htmlFor="pc-name" className="sm:col-span-2"><input id="pc-name" value={form.name} onChange={set('name')} maxLength={60} className={inputClass} /></Field>
        <Field label="Kind" htmlFor="pc-kind">
          <select
            id="pc-kind"
            value={form.kind}
            onChange={(event) => {
              const kind = event.target.value as PayComponent['kind'];
              // An earning is a fixed amount or a share of the basic, never by slab nor of the gross
              setForm((previous) => ({ ...previous, kind, ...(kind === 'earning' ? { method: previous.method === 'slab' ? 'fixed' : previous.method, base: 'basic' as const } : {}) }));
            }}
            className={inputClass}
          ><option value="earning">Earning</option><option value="deduction">Deduction</option><option value="employer">Paid by the employer on top</option></select>
        </Field>
        <Field label="Worked out as" htmlFor="pc-method">
          <select id="pc-method" value={form.method} onChange={set('method')} className={inputClass}><option value="fixed">A fixed amount</option><option value="percent">A percentage</option>{form.kind !== 'earning' && <option value="slab">By slab of the gross</option>}</select>
        </Field>
        <Field label="For" htmlFor="pc-applies">
          <select id="pc-applies" value={form.appliesTo} onChange={set('appliesTo')} className={inputClass}><option value="all">Everyone</option><option value="monthly">Monthly pay only</option><option value="daily">Daily wages only</option></select>
        </Field>
        {form.method === 'fixed' && (
          <>
            <Field label="Default amount (₹)" htmlFor="pc-value" hint="Each employee can have their own. For daily wages: per day paid"><input id="pc-value" type="number" min={0} step="0.01" value={form.value} onChange={set('value')} className={inputClass} /></Field>
            <label className="mt-6 flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={form.prorate} onChange={(event) => setForm((p) => ({ ...p, prorate: event.target.checked }))} className="h-4 w-4 rounded border-gray-300 text-indigo-600" /> In proportion to the days paid</label>
          </>
        )}
        {form.method === 'percent' && (
          <>
            <Field label="Percentage" htmlFor="pc-value"><input id="pc-value" type="number" min={0} max={100} step="0.0001" value={form.value} onChange={set('value')} className={inputClass} /></Field>
            <Field label="Of" htmlFor="pc-base">
              <select id="pc-base" value={form.base} onChange={set('base')} className={inputClass}><option value="basic">The basic</option>{form.kind !== 'earning' && <option value="gross">The gross</option>}</select>
            </Field>
            <Field label="Counted up to (₹)" htmlFor="pc-ceiling" hint="Optional ceiling of the base"><input id="pc-ceiling" type="number" min={0} step="0.01" value={form.ceiling} onChange={set('ceiling')} className={inputClass} /></Field>
          </>
        )}
        {form.kind !== 'earning' && (
          <Field label="Only for a gross up to (₹)" htmlFor="pc-eligible" hint="Optional"><input id="pc-eligible" type="number" min={0} step="0.01" value={form.eligibleUpToGross} onChange={set('eligibleUpToGross')} className={inputClass} /></Field>
        )}
        <Field label="Order on the payslip" htmlFor="pc-sequence"><input id="pc-sequence" type="number" min={0} max={999} value={form.sequence} onChange={set('sequence')} className={inputClass} /></Field>
      </div>
      {form.method === 'slab' && (
        <div className="rounded-md bg-gray-50 p-3 ring-1 ring-gray-200">
          <p className="text-sm font-medium text-gray-900">Slabs of the gross</p>
          {slabs.map((slab, index) => (
            <div key={index} className="mt-2 flex items-end gap-3">
              <Field label={index === slabs.length - 1 ? 'Above that' : 'Gross up to (₹)'} htmlFor={`slab-up-${index}`}>
                <input id={`slab-up-${index}`} type="number" min={0} disabled={index === slabs.length - 1} value={index === slabs.length - 1 ? '' : slab.upTo} onChange={(event) => setSlabs((list) => list.map((item, i) => (i === index ? { ...item, upTo: event.target.value } : item)))} className={inputClass} />
              </Field>
              <Field label="Amount (₹)" htmlFor={`slab-amount-${index}`}>
                <input id={`slab-amount-${index}`} type="number" min={0} step="0.01" value={slab.amount} onChange={(event) => setSlabs((list) => list.map((item, i) => (i === index ? { ...item, amount: event.target.value } : item)))} className={inputClass} />
              </Field>
              {slabs.length > 1 && <button type="button" onClick={() => setSlabs((list) => list.filter((_, i) => i !== index))} aria-label="Remove slab" className="mb-2 text-gray-400 hover:text-red-600"><TrashIcon className="h-5 w-5" /></button>}
            </div>
          ))}
          <button type="button" onClick={() => setSlabs((list) => [...list.slice(0, -1), { upTo: '', amount: '' }, list[list.length - 1]])} className="mt-2 text-sm font-medium text-indigo-600">Add a slab</button>
        </div>
      )}
      <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={form.isActive} onChange={(event) => setForm((p) => ({ ...p, isActive: event.target.checked }))} className="h-4 w-4 rounded border-gray-300 text-indigo-600" /> In use</label>
      <FormActions>
        <button type="button" onClick={onDone} className={secondaryButtonClass}>Cancel</button>
        <button type="submit" disabled={save.isPending} className={primaryButtonClass}>{save.isPending ? 'Saving…' : 'Save Component'}</button>
      </FormActions>
    </form>
  );
};

const describe = (component: PayComponent) => {
  if (component.method === 'fixed') return `Fixed, default ${formatMoney(Number(component.value))}${component.prorate ? ', by days paid' : ''}`;
  if (component.method === 'percent') return `${Number(component.value)}% of the ${component.base}${component.ceiling ? ` up to ${formatMoney(Number(component.ceiling))}` : ''}`;
  return (component.slabs ?? []).map((slab) => `${slab.upTo === null ? 'above' : `≤ ${formatMoney(slab.upTo)}`}: ${formatMoney(slab.amount)}`).join('; ');
};

const HrSetup: React.FC<{ canEdit: boolean }> = ({ canEdit }) => {
  const queryClient = useQueryClient();
  const { data: shifts = [] } = useShifts();
  const { data: contractors = [] } = useContractors();
  const { data: components = [] } = useComponents();
  const [year, setYear] = useState(new Date().getFullYear());
  const { data: holidays = [] } = useQuery<{ id: string; date: string; name: string }[]>({ queryKey: ['hr', 'holidays', year], queryFn: async () => (await api.get('/hr/holidays', { params: { year } })).data });
  const [editing, setEditing] = useState<{ kind: 'shift' | 'contractor' | 'component'; item: any } | null>(null);
  const [holiday, setHoliday] = useState({ date: '', name: '' });
  const addHoliday = useMutation({
    mutationFn: async () => api.post('/hr/holidays', holiday),
    onSuccess: () => {
      // Show the year the holiday was added to
      if (holiday.date) setYear(Number(holiday.date.slice(0, 4)));
      setHoliday({ date: '', name: '' });
      queryClient.invalidateQueries({ queryKey: ['hr', 'holidays'] });
    },
    onError: failed('Could not add the holiday'),
  });
  const removeHoliday = useMutation({
    mutationFn: async (id: string) => api.delete(`/hr/holidays/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['hr', 'holidays'] }),
    onError: failed('Could not remove the holiday'),
  });
  const addButton = (kind: 'shift' | 'contractor' | 'component', label: string) =>
    canEdit && (
      <button type="button" onClick={() => setEditing({ kind, item: null })} className={secondaryButtonClass}>
        <PlusIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
        {label}
      </button>
    );

  return (
    <div className="space-y-6">
      <Section title="How payroll is worked out" description="The company's own rules. Nothing statutory is built in.">
        <PayrollSettingsForm canEdit={canEdit} />
      </Section>

      <Section title="Pay components" description="Earnings, deductions (PF, ESI, professional tax…) and what the employer pays on top, with your own rates, ceilings and slabs." action={addButton('component', 'Add Component')}>
        {components.length === 0 ? <p className="p-4 text-sm text-gray-500">None yet. Payslips show only the basic until components are added.</p> : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50"><tr><th className={thClass}>Code</th><th className={thClass}>Name</th><th className={thClass}>Kind</th><th className={thClass}>Worked out as</th><th className={thClass}>Limits</th><th className={thClass}><span className="sr-only">Edit</span></th></tr></thead>
            <tbody className="divide-y divide-gray-100">
              {components.map((component) => (
                <tr key={component.id} data-testid="pay-component">
                  <td className={cn(tdClass, 'font-mono')}>{component.code}</td>
                  <td className={tdClass}>{component.name}{!component.isActive && <span className={cn(badgeClass, 'ml-2 bg-gray-100 text-gray-500')}>Not in use</span>}</td>
                  <td className={tdClass}>{component.kind === 'employer' ? 'Employer' : component.kind === 'earning' ? 'Earning' : 'Deduction'}{component.appliesTo !== 'all' ? ` · ${component.appliesTo} only` : ''}</td>
                  <td className={tdClass}>{describe(component)}</td>
                  <td className={tdClass}>{component.eligibleUpToGross ? `Gross up to ${formatMoney(Number(component.eligibleUpToGross))}` : '—'}</td>
                  <td className={cn(tdClass, 'text-right')}>{canEdit && <button type="button" onClick={() => setEditing({ kind: 'component', item: component })} className="text-sm font-medium text-indigo-600">Edit</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Section title="Shifts" description="Hours, breaks and when someone counts as late." action={addButton('shift', 'Add Shift')}>
          <table className="min-w-full divide-y divide-gray-200">
            <tbody className="divide-y divide-gray-100">
              {shifts.map((shift) => (
                <tr key={shift.id}>
                  <td className={cn(tdClass, 'font-mono')}>{shift.code}</td>
                  <td className={tdClass}>{shift.name}{!shift.isActive && ' (not in use)'}</td>
                  <td className={tdClass}>{shift.startTime}–{shift.endTime}, break {shift.breakMinutes} min · {hours(shift.workMinutes)} h of work</td>
                  <td className={cn(tdClass, 'text-right')}>{canEdit && <button type="button" onClick={() => setEditing({ kind: 'shift', item: shift })} className="text-sm font-medium text-indigo-600">Edit</button>}</td>
                </tr>
              ))}
              {shifts.length === 0 && <tr><td className="p-4 text-sm text-gray-500">No shifts yet.</td></tr>}
            </tbody>
          </table>
        </Section>

        <Section
          title={`Holidays ${year}`}
          description="Suggested on the attendance sheet of their day."
          action={
            <span className="flex gap-1">
              <button type="button" onClick={() => setYear(year - 1)} aria-label="Previous year" className={secondaryButtonClass}>‹</button>
              <button type="button" onClick={() => setYear(year + 1)} aria-label="Next year" className={secondaryButtonClass}>›</button>
            </span>
          }
        >
          <ul className="divide-y divide-gray-100">
            {holidays.map((item) => (
              <li key={item.id} className="flex items-center justify-between px-4 py-2 text-sm">
                <span>{day(item.date)} · {item.name}</span>
                {canEdit && <button type="button" onClick={() => removeHoliday.mutate(item.id)} aria-label={`Remove ${item.name}`} className="text-gray-400 hover:text-red-600"><TrashIcon className="h-4 w-4" /></button>}
              </li>
            ))}
            {holidays.length === 0 && <li className="p-4 text-sm text-gray-500">No holidays yet.</li>}
          </ul>
          {canEdit && (
            <form className="flex flex-wrap items-end gap-3 border-t border-gray-200 p-4" onSubmit={(event) => { event.preventDefault(); if (holiday.date && holiday.name.trim()) addHoliday.mutate(); }}>
              <Field label="Date" htmlFor="ho-date"><input id="ho-date" type="date" value={holiday.date} onChange={(event) => setHoliday((p) => ({ ...p, date: event.target.value }))} className={inputClass} /></Field>
              <Field label="Name" htmlFor="ho-name" className="flex-1"><input id="ho-name" value={holiday.name} maxLength={100} onChange={(event) => setHoliday((p) => ({ ...p, name: event.target.value }))} className={inputClass} /></Field>
              <button type="submit" disabled={addHoliday.isPending} className={secondaryButtonClass}>Add Holiday</button>
            </form>
          )}
        </Section>
      </div>

      <Section title="Labour contractors" description="Who supplies contract workers, their licence and what they charge." action={addButton('contractor', 'Add Contractor')}>
        <table className="min-w-full divide-y divide-gray-200">
          <tbody className="divide-y divide-gray-100">
            {contractors.map((contractor) => (
              <tr key={contractor.id}>
                <td className={cn(tdClass, 'font-mono')}>{contractor.code}</td>
                <td className={tdClass}>{contractor.name}{!contractor.isActive && ' (not in use)'}</td>
                <td className={tdClass}>{contractor.licenceNumber ? `Licence ${contractor.licenceNumber}${contractor.licenceValidUntil ? ` until ${day(contractor.licenceValidUntil)}` : ''}` : 'No licence entered'}</td>
                <td className={tdClass}>{contractor.ratePerManDay ? `${formatMoney(Number(contractor.ratePerManDay))} a man-day` : 'No rate agreed'}</td>
                <td className={tdClass}>{contractor.workers} worker(s)</td>
                <td className={cn(tdClass, 'text-right')}>{canEdit && <button type="button" onClick={() => setEditing({ kind: 'contractor', item: contractor })} className="text-sm font-medium text-indigo-600">Edit</button>}</td>
              </tr>
            ))}
            {contractors.length === 0 && <tr><td className="p-4 text-sm text-gray-500">No contractors yet.</td></tr>}
          </tbody>
        </table>
      </Section>

      <Modal isOpen={!!editing} onClose={() => setEditing(null)} title={editing ? `${editing.item ? 'Edit' : 'Add'} ${editing.kind === 'component' ? 'Pay Component' : editing.kind === 'shift' ? 'Shift' : 'Contractor'}` : ''} size="xl">
        {editing?.kind === 'shift' && <ShiftForm shift={editing.item} onDone={() => setEditing(null)} />}
        {editing?.kind === 'contractor' && <ContractorForm contractor={editing.item} onDone={() => setEditing(null)} />}
        {editing?.kind === 'component' && <ComponentForm component={editing.item} onDone={() => setEditing(null)} />}
      </Modal>
    </div>
  );
};

export default HrSetup;
